import { getContentfulClient } from "@tiicker/util/lib/contentful/client";
import {
  ContentSections,
  getImageReference,
  getItemReference,
  loadContentSections,
} from "@tiicker/util/lib/contentful/helpers";
import { FeaturedNews } from "@tiicker/util/lib/contentful/queries";
import {
  ContentfulContentType,
  ContentfulKey,
  GenericMarketingPage,
  PageSection,
  PageSectionKey,
  PressRelease,
} from "@tiicker/util/lib/contentful/types";
import Container from "components/core/Layout/Container/Container";
import FooterV2 from "components/FooterV2/FooterV2";
import GeneralPageHeader from "components/Global/Headers/GeneralPageHeader/GeneralPageHeader";
import NavigationBar from "components/Global/Navigation/NavigationBar/NavigationBar";
import ColorWrapper from "components/Home/ColorWrapper/ColorWrapper";
import GenericMetaTags from "components/MetaTags/GenericMetaTags";
import NewsCallToAction from "components/News/CallToAction/NewsCallToAction";
import FeaturedNewsSection from "components/News/FeaturedNewsSection/FeaturedNewsSection";
import MediaInquiry from "components/News/MediaInquiry/MediaInquiry";
import NewsSection from "components/News/NewsSection/NewsSection";
import PressReleaseSection from "components/News/PressReleaseSection/PressReleaseSection";
import StartEarningPerksMadi from "components/StartEarningPerksMadi/StartEarningPerksMadi";
import { Entry, EntryCollection } from "contentful";
import { compact } from "lodash";
import { GetStaticProps } from "next";
import React from "react";

type Props = {
  pageContent: EntryCollection<GenericMarketingPage>;
  pageSections: Entry<PageSection>[];
  contentSections: ContentSections;
  featuredNews: EntryCollection<PressRelease>;
};

const news = (props: Props) => {
  const page =
    props.pageContent.items.length > 0 ? props.pageContent.items[0] : undefined;

  const mediaInquiry = props.pageSections.find(
    (section) => section.fields.sectionKey === PageSectionKey.NewsMediaQuery
  );

  const newsCTA = props.pageSections.find(
    (section) => section.fields.sectionKey === PageSectionKey.NewsCTA
  );
  const cta = props.pageSections.find(
    (x) => x.fields.sectionKey === PageSectionKey.JoinNowCTA
  );

  return (
    <>
      {page && <GenericMetaTags pageContent={page.fields} />}
      <ColorWrapper displayOptions={{ marginBottom: 120 }}>
        <NavigationBar />
        <GeneralPageHeader
          title={page?.fields.displayTitle ?? ""}
          description={page?.fields.displayDescription ?? ""}
        />
      </ColorWrapper>
      <Container>
        <FeaturedNewsSection news={props.featuredNews} />
        <PressReleaseSection />
        {mediaInquiry && (
          <MediaInquiry
            description={mediaInquiry.fields.description}
            imageUrl={
              getImageReference(mediaInquiry.fields.image)?.fields.file.url ??
              ""
            }
          />
        )}
        <NewsSection
          excludedNewsIds={props.featuredNews.items.map((x) => x.sys.id)}
        />
        {newsCTA && (
          <NewsCallToAction
            callToAction={newsCTA}
            contentSections={props.contentSections}
          />
        )}
      </Container>
      {cta && (
        <StartEarningPerksMadi
          page={cta}
          includes={props.contentSections.includes}
        />
      )}
      <FooterV2 />
    </>
  );
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  const client = getContentfulClient("contentful");
  const newsContent = await client.getEntries<GenericMarketingPage>({
    content_type: ContentfulContentType.GenericMarketingPage,
    ["fields.key"]: ContentfulKey.News,
  });

  const news = await client.getEntries<PressRelease>(FeaturedNews());

  const page = newsContent.items.length > 0 ? newsContent.items[0] : null;
  const pageSections = compact(
    page?.fields.pageSections?.map((section) =>
      getItemReference(section, newsContent.includes)
    )
  );
  const contentSections = await loadContentSections(client, pageSections);

  return {
    props: {
      pageContent: newsContent,
      pageSections,
      contentSections,
      featuredNews: news,
    },
    revalidate: 100,
  };
};

export default news;
